import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Modal,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useLazyListAllFeatureFlagsQuery } from '@/store/api/admin/featureFlagsApi';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { SVG_COMPONENTS } from '@/assets/admin';
import { GradientBackground } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useFeatureFlagActions } from './hooks/useFeatureFlagActions';
import { FlagDetailsContent } from './components/FlagDetailsContent';

interface FeatureFlagsDetailsSidebarProps {
  selectedFeatureFlag?: FeatureFlagAttributes;
  onClose: () => void;
  isOrganizationView: boolean;
}

export const FeatureFlagsDetailsSidebar: React.FC<FeatureFlagsDetailsSidebarProps> = ({
  selectedFeatureFlag,
  onClose,
  isOrganizationView,
}) => {
  const { redesign } = useAdminColors();
  const [getFeatureFlags] = useLazyListAllFeatureFlagsQuery();
  const [localFlags, setLocalFlags] = useState(selectedFeatureFlag?.flags || {});

  const {
    editingKey,
    editValue,
    setEditValue,
    handleResetFlag,
    handleSubmitValue,
    startEditing,
    cancelEditing,
  } = useFeatureFlagActions({
    selectedFlag: selectedFeatureFlag,
    isOrganizationView,
    localFlags,
    setLocalFlags,
  });

  useEffect(() => {
    setLocalFlags(selectedFeatureFlag?.flags || {});
  }, [selectedFeatureFlag]);

  const handleClose = () => {
    getFeatureFlags();
    onClose();
  };

  if (!selectedFeatureFlag) return null;

  return (
    <Modal
      visible={!!selectedFeatureFlag}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <GradientBackground>
        <KeyboardAvoidingView
          style={styles.container}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View style={[styles.header, { borderBottomColor: redesign.card.border }]}>
            <View style={styles.headerContent}>
              {isOrganizationView ? (
                <Text style={[styles.title, { color: redesign.text.primary }]}>
                  {selectedFeatureFlag.organizationName}
                </Text>
              ) : (
                <>
                  <Text style={[styles.title, { color: redesign.text.primary }]}>
                    {selectedFeatureFlag.propertyName}
                  </Text>
                  <Text style={[styles.subtitle, { color: redesign.text.secondary }]}>
                    {selectedFeatureFlag.organizationName}
                  </Text>
                </>
              )}
            </View>
            <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
              <SVG_COMPONENTS.CANCEL_X
                width={20}
                height={20}
                color={redesign.text.primary}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <FlagDetailsContent
              localFlags={localFlags}
              editingKey={editingKey}
              editValue={editValue}
              setEditValue={setEditValue}
              onSubmitValue={handleSubmitValue}
              onCancelEditing={cancelEditing}
              onStartEditing={startEditing}
              onResetFlag={handleResetFlag}
            />
          </View>
        </KeyboardAvoidingView>
      </GradientBackground>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  closeButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    padding: 16,
  },
});
